/* PresetReports — /admin/reports
   - Square card layout with aggregate data per report
   - Download Data button (streams CSV from backend)
   - Edit modal with name + description + aggregate_query + select_query
   - Create / Delete support */
import { useState, useEffect } from 'react';
import css from '../AdminPage.module.css';
import {
  usePresetReports,
  useReportAggregate,
  useCreateReport,
  useUpdateReport,
  useDeleteReport,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import { usePermissions } from '../../../hooks/usePermissions';
import type { PresetReport } from '../../../types/admin';
import api from '../../../services/api';

/* ── Aggregate card for a single report ── */
function ReportCard({
  report,
  onEdit,
  onDelete,
}: {
  report: PresetReport;
  onEdit: (r: PresetReport) => void;
  onDelete: (id: number) => void;
}) {
  const { data: agg } = useReportAggregate(report.id);
  const { showToast } = useToast();
  const { isSuperAdmin, hasPermission } = usePermissions();
  const canDownload = isSuperAdmin || hasPermission('export_data');
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await api.get(`/api/preset-reports/${report.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      let message = 'Download failed';
      try {
        const data = (err as { response?: { data?: Blob } }).response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          const json = JSON.parse(text) as { detail?: string; message?: string };
          if (json.detail) message = `Download failed: ${json.detail}`;
        }
      } catch {
        // couldn't parse error body — use default message
      }
      showToast(message, 'error');
    }
    setDownloading(false);
  }

  /* Discover columns dynamically from aggregate data rows */
  const aggRows = agg?.data ?? [];
  const columns = aggRows.length > 0 ? Object.keys(aggRows[0]) : [];

  return (
    <div
      style={{
        border: '1px solid var(--gray-200)',
        borderRadius: 'var(--radius-md)',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: 200,
        background: 'var(--surface-primary)',
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
        transition: 'box-shadow 0.15s',
      }}
    >
      {/* Header */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 'var(--font-size-base)', marginBottom: 4 }}>
          {report.name}
        </div>
        {report.description && (
          <div
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginBottom: 8 }}
          >
            {report.description}
          </div>
        )}
      </div>

      {/* Aggregate table (full data display) */}
      <div
        style={{
          maxHeight: 350,
          overflowY: 'auto',
          overflowX: 'auto',
          margin: '8px 0',
          border: aggRows.length > 0 ? '1px solid var(--gray-200)' : 'none',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        {aggRows.length > 0 ? (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}
          >
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: '4px 8px',
                      background: 'var(--gray-50, #f8fafc)',
                      borderBottom: '1px solid var(--gray-200)',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    {col.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aggRows.map((row, ri) => (
                <tr key={ri}>
                  {columns.map((col) => (
                    <td
                      key={col}
                      style={{
                        padding: '3px 8px',
                        borderBottom: '1px solid var(--gray-100)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row[col] != null ? String(row[col]) : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: 16,
              color: 'var(--gray-400)',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            {agg ? 'No aggregate data' : 'Loading...'}
          </div>
        )}
      </div>

      {/* Row count badge */}
      {agg && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--gray-500)',
            textAlign: 'center',
            marginBottom: 8,
          }}
        >
          {agg.rowCount} row{agg.rowCount !== 1 ? 's' : ''}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
        <button
          className={css.btnPrimary}
          style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }}
          onClick={handleDownload}
          disabled={downloading || !canDownload}
          title={!canDownload ? 'Permission Blocked' : undefined}
        >
          {downloading ? 'Downloading…' : '⬇ Download Data'}
        </button>
        <button
          className={css.btnSecondary}
          style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}
          onClick={() => onEdit(report)}
        >
          ✏️ Edit
        </button>
        <button
          className={css.btnDanger}
          style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}
          onClick={() => onDelete(report.id)}
        >
          Del
        </button>
      </div>
    </div>
  );
}

/* ── Main component ── */
export default function PresetReports() {
  const { data: reports = [], isLoading } = usePresetReports();
  const createReport = useCreateReport();
  const updateReport = useUpdateReport();
  const deleteReport = useDeleteReport();
  const { showToast } = useToast();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PresetReport | null>(null);

  /* form */
  const [rName, setRName] = useState('');
  const [rDesc, setRDesc] = useState('');
  const [rAggQ, setRAggQ] = useState('');
  const [rSelQ, setRSelQ] = useState('');

  useEffect(() => {
    if (editing) {
      setRName(editing.name);
      setRDesc(editing.description || '');
      setRAggQ(editing.aggregate_query || '');
      setRSelQ(editing.select_query || '');
    }
  }, [editing]);

  function openNew() {
    setEditing(null);
    setRName('');
    setRDesc('');
    setRAggQ('');
    setRSelQ('');
    setShowModal(true);
  }

  function openEdit(r: PresetReport) {
    setEditing(r);
    setShowModal(true);
  }

  async function handleDelete(id: number) {
    if (!confirm('Delete this report?')) return;
    try {
      await deleteReport.mutateAsync(id);
      showToast('Report deleted', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    }
  }

  async function save() {
    const payload = {
      name: rName,
      description: rDesc,
      aggregate_query: rAggQ,
      select_query: rSelQ,
    };
    try {
      if (editing) {
        await updateReport.mutateAsync({ id: editing.id, ...payload });
        showToast('Report updated', 'success');
      } else {
        await createReport.mutateAsync(payload);
        showToast('Report created', 'success');
      }
      setShowModal(false);
    } catch {
      showToast('Failed to save report', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>📈 Preset Reports</h2>
            <p className={css.sectionSubtitle}>
              {reports.length} report{reports.length !== 1 ? 's' : ''} available
            </p>
          </div>
          <button className={css.btnPrimary} onClick={openNew}>
            + New Report
          </button>
        </div>

        {reports.length === 0 ? (
          <div className={css.emptyState}>
            <div className={css.emptyIcon}>📈</div>
            <p className={css.emptyMessage}>No preset reports configured</p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
              gap: 16,
            }}
          >
            {reports.map((r) => (
              <ReportCard key={r.id} report={r} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>

      {/* Edit / Create modal */}
      {showModal && (
        <div className={css.modalBackdrop} onClick={() => setShowModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>{editing ? 'Edit Report' : 'New Report'}</h3>
              <button className={css.modalClose} onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Report Name</label>
                <input
                  className={css.formInput}
                  value={rName}
                  onChange={(e) => setRName(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Description</label>
                <textarea
                  className={css.formTextarea}
                  value={rDesc}
                  onChange={(e) => setRDesc(e.target.value)}
                  rows={2}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Aggregate Query (SQL)</label>
                <textarea
                  className={css.formTextarea}
                  value={rAggQ}
                  onChange={(e) => setRAggQ(e.target.value)}
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}
                  placeholder="SELECT COUNT(*) as total_rows FROM ..."
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Select Query (CSV download)</label>
                <textarea
                  className={css.formTextarea}
                  value={rSelQ}
                  onChange={(e) => setRSelQ(e.target.value)}
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}
                  placeholder="SELECT * FROM ..."
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={save} disabled={!rName}>
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
